/**
 * Les gestes de l'onglet « Offres de cours » et ses trois écritures : accepter une offre, la masquer,
 * retirer sa candidature (`updateGroupAttributionProposalStatus` pour un groupe ou un essai,
 * `updateSubstitutionProposalStatus` pour un remplacement).
 *
 * Port de `ProposalsTabScreen.js` :
 *  - toucher une offre en attente l'ouvre (l'offre, puis la confirmation) ; toucher une offre
 *    candidatée propose de retirer la candidature ; les autres ne se touchent pas ;
 *  - après une réponse, seuls les compteurs sont relus : le réducteur met la ligne à jour sur place ;
 *  - le rappel « Confirme tes créneaux » pousse « Ma dispo » ;
 *  - un « Changement de créneau » (`handleSlotChange`) s'ouvre sur le choix de ses horaires, s'accepte
 *    avec eux (`live_session_ids`), et « Je ne peux pas » se confirme en disant ce qu'il déclenche.
 * Et ce que l'accueil demande : « Accepter » et « Voir » d'une offre de « Ma journée » arrivent ici
 * avec l'offre (`offerKind`, `offerId`, `offerStep`), qui s'ouvre dès que la liste la montre.
 * Mesurés : `offer.accept` (ouvert avec l'offre), `offer.hide` (ouvert avec la boîte de « Je ne peux
 * pas » d'un changement de créneau), `offer.withdraw` (ouvert avec sa boîte).
 */
import { useNavigation } from "@react-navigation/native"
import { useEffect, useReducer } from "react"
import { useDispatch } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { groupAttributionProposalsService } from "../../../../../../data/services/groupAttributionProposalsService"
import { groupAttributionService } from "../../../../../../data/services/groupAttributionService"
import { substitutionProposalsService } from "../../../../../../data/services/substitutionProposalsService"
import { type TutorOffersViewModel } from "../../../../view-models/tutor/Availability/tutorOffersViewModel"
import { type UITutorOffer, type UITutorOfferFlowStep, type UITutorOfferKind } from "../../../types/tutorDispoOffres"
import {
    initialTutorOffersUiState,
    slotChoiceFlowProps,
    tutorOfferInFlow,
    tutorOfferAcceptBody,
    tutorOffersUiReducer,
    tutorOfferToOpen,
    type TutorOffersUiAction,
    type TutorOffersUiState,
} from "./tutorOffersUiState"
import { type TutorOfferAnswer, useTutorOfferAnswers } from "./useTutorOfferAnswers"
import type { TutorOfferFlowProps } from "../../../../../../design-system/templates/TutorOfferFlow"

const SURFACE = "tutor_offers"

const KINDS: readonly UITutorOfferKind[] = ["group", "trial", "substitution"]
const STEPS: readonly UITutorOfferFlowStep[] = ["detail", "confirm", "withdraw"]

/** Ce que « Ma journée » passe à l'onglet pour ouvrir une offre. */
export type TutorOffersRouteParams = { offerKind?: string; offerId?: number | string; offerStep?: string }

type OffersNavigation = {
    push: (route: string, params?: object) => void
    setParams: (params: object) => void
}

/** L'onglet demandé et la feuille : lus par le view-model, avant les gestes. */
export function useTutorOffersUi() {
    return useReducer(tutorOffersUiReducer, initialTutorOffersUiState)
}

export function useTutorOffersState(
    vm: TutorOffersViewModel,
    ui: TutorOffersUiState,
    dispatchUi: (action: TutorOffersUiAction) => void,
    params: TutorOffersRouteParams | undefined
) {
    const navigation = useNavigation() as unknown as OffersNavigation
    const dispatch = useDispatch()

    const callbacks = {
        onSuccess: () => {
            // La ligne est mise à jour par le réducteur ; seuls les compteurs se relisent.
            dispatch(apiRequest(groupAttributionService.getProposalsCount) as never)
            dispatchUi({ type: "SENT" })
        },
        onError: (_answer: TutorOfferAnswer, message: string) => dispatchUi({ type: "FAILED", message }),
    }
    const groupAnswers = useTutorOfferAnswers(
        groupAttributionProposalsService.updateGroupAttributionProposalStatus,
        SURFACE,
        "id",
        callbacks
    )
    const substitutionAnswers = useTutorOfferAnswers(
        substitutionProposalsService.updateSubstitutionProposalStatus,
        SURFACE,
        "id",
        callbacks
    )
    const answersOf = (offer: UITutorOffer) => (offer.kind === "substitution" ? substitutionAnswers : groupAnswers)

    const send = (offer: UITutorOffer, answer: TutorOfferAnswer, body: Record<string, unknown> = {}) => {
        if (ui.sending != null) return
        if (answersOf(offer).start(answer, { id: offer.id }, body)) {
            dispatchUi({ type: "SENDING", offerId: offer.id, answer })
        }
    }

    const accept = (offer: UITutorOffer) => {
        // Un changement de créneau ne part qu'avec ses horaires, autant que de cours qui changent.
        const body = tutorOfferAcceptBody(offer, ui.slotSelection)
        if (body != null) send(offer, "accept", body)
    }

    const open = (offer: UITutorOffer, step: UITutorOfferFlowStep) => {
        answersOf(offer).opened(step === "withdraw" ? "withdraw" : "accept")
        dispatchUi({ type: "OPEN_FLOW", offerId: offer.id, step, slotSelection: offer.slotChange?.initialSelection })
    }

    // « Accepter » / « Voir » de l'accueil : l'offre est demandée une fois, puis la route oublie la demande.
    const requestedId = params?.offerId != null ? Number(params.offerId) : null
    const requestedKind = KINDS.find((kind) => kind === params?.offerKind) ?? null
    const requestedStep = STEPS.find((step) => step === params?.offerStep) ?? "detail"
    useEffect(() => {
        if (requestedId == null || Number.isNaN(requestedId)) return
        dispatchUi({
            type: "REQUEST_OPEN",
            kind: requestedKind,
            request: { offerId: requestedId, step: requestedStep },
        })
        navigation.setParams({ offerKind: undefined, offerId: undefined, offerStep: undefined })
    }, [requestedId, requestedKind, requestedStep, navigation, dispatchUi])

    const toOpen = tutorOfferToOpen(ui, vm.offers)
    useEffect(() => {
        if (toOpen == null || ui.pendingOpen == null) return
        open(toOpen, ui.pendingOpen.step)
        // `open` est reconstruit à chaque rendu ; l'offre trouvée suffit à décider.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toOpen])

    const flowOffer = tutorOfferInFlow(ui, vm.offers)
    const flow: TutorOfferFlowProps | null =
        flowOffer != null && ui.flow != null
            ? {
                  visible: true,
                  onClose: () => dispatchUi({ type: "CLOSE_FLOW" }),
                  offer: flowOffer,
                  step: ui.flow.step,
                  onAccept: () => dispatchUi({ type: "GO_STEP", step: "confirm" }),
                  onHide: () => send(flowOffer, "hide"),
                  onConfirm: () => accept(flowOffer),
                  onWithdraw: () => send(flowOffer, "withdraw"),
                  onBack: () => dispatchUi({ type: "GO_STEP", step: "detail" }),
                  ...slotChoiceFlowProps(flowOffer, ui.slotSelection, dispatchUi),
                  onDecline: () => {
                      // « Je ne peux pas » se confirme : l'ouverture de sa boîte se mesure, comme celle d'un retrait.
                      answersOf(flowOffer).opened("hide")
                      dispatchUi({ type: "GO_STEP", step: "decline" })
                  },
                  sending: ui.sending?.offerId === flowOffer.id ? ui.sending.answer : null,
                  errorMessage: ui.flowError ?? undefined,
              }
            : null

    return {
        onSelectTab: (kind: UITutorOfferKind) => dispatchUi({ type: "SELECT_TAB", kind }),
        onPressOffer: (offer: UITutorOffer) => {
            if (offer.status === "pending") open(offer, "detail")
            else if (offer.status === "accepted") open(offer, "withdraw")
        },
        sendingIds: ui.sending != null ? [ui.sending.offerId] : [],
        onOpenDispo: () => navigation.push("tutor/ma-dispo"),
        flow,
    }
}
